import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { XCircle, Eye, Trash2, Mail, Shield, ArrowLeft } from "lucide-react"
import { useTranslation } from "@/lib/i18n"

import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'

export const Route = createFileRoute('/data-deletion')({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: 'Data Deletion - Zapify | GDPR Compliant',
        description: 'Learn how to request the deletion of your personal data from Zapify. GDPR compliant data deletion process.',
        url: 'https://zapify.pro/data-deletion',
      }),
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/data-deletion" }
    ],
    scripts: [
      createWebPageSchema({
        name: 'Data Deletion - Zapify',
        description: 'Learn how to request the deletion of your personal data from Zapify.',
        url: 'https://zapify.pro/data-deletion',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: 'Home', item: 'https://zapify.pro' },
          { position: 2, name: 'Data Deletion', item: 'https://zapify.pro/data-deletion' },
        ],
      }),
    ],
  }),
  component: DataDeletionPage,
})

export function DataDeletionPage() {
  const { translations: t } = useTranslation();
  return (
    <main className="min-h-screen bg-white dark:bg-black py-20 px-4 md:px-8 transition-colors">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <Badge variant="outline" className="px-4 py-1 border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 mb-2">
            {t.legal.lastUpdated}: July 1, 2025
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-rose-600 dark:from-red-400 dark:to-rose-400">
            {t.legal.dataDeletion}
          </h1>
          
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <Badge
              variant="outline"
              className="bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900"
            >
              Instagram App Data
            </Badge>
            <Badge
              variant="outline"
              className="bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900"
            >
              User Privacy
            </Badge>
            <Badge
              variant="outline"
              className="bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-900"
            >
              GDPR Compliant
            </Badge>
          </div>

          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
             If you would like to delete <strong>Zapify</strong> app from
            your Instagram account and revoke its access to your data,
            please follow these steps:
          </p>
        </div>

        <Card className="border-gray-200 dark:border-neutral-800 shadow-lg bg-white dark:bg-neutral-900">
          <CardContent className="p-8 md:p-12 space-y-12">
            
            {/* Section 1 */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    1. Removing the App from Your Instagram Account
                  </h2>
                </div>
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900 rounded-lg p-6">
                  <ol className="list-decimal pl-6 space-y-3 text-gray-700 dark:text-gray-300 text-base">
                    <li>
                      Open the <strong>Instagram app</strong> and go to{" "}
                      <strong>Settings</strong>.
                    </li>
                    <li>
                      Navigate to the <strong>"Your App and Media"</strong>{" "}
                      section.
                    </li>
                    <li>
                      Tap on <strong>"Website Permissions"</strong> (or directly
                      open{" "}
                      <a
                        href="https://www.instagram.com/accounts/manage_access/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        this link
                      </a>{" "}
                      in your browser).
                    </li>
                    <li>
                      Under the <strong>Active</strong> tab, locate{" "}
                      <strong>"Zapify"</strong> app.
                    </li>
                    <li>
                      Tap on <strong>"Remove"</strong>, and confirm your choice
                      again to disconnect the app from your account.
                    </li>
                  </ol>
                </div>
            </section>

             <Separator className="bg-gray-100 dark:bg-neutral-800" />

            {/* Section 2 */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    2. How We Handle Data & Automatic Revocation
                  </h2>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900 rounded-lg p-6">
                  <p className="text-gray-700 dark:text-gray-300 text-base mb-2">
                    Zapify only collects Instagram data on pages you have liked.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-base">
                    Access to this data is automatically revoked{" "}
                    <strong>two months after your last interaction</strong> with
                    the app.
                  </p>
                </div>
            </section>

             <Separator className="bg-gray-100 dark:bg-neutral-800" />

            {/* Section 3 */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Trash2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    3. Requesting Complete Data Deletion
                  </h2>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-900 rounded-lg p-6">
                  <p className="text-gray-700 dark:text-gray-300 text-base mb-4">
                    If you would like all associated Instagram data permanently
                    deleted, you can submit a request by contacting us:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 text-base mb-6">
                    <li className="flex items-center gap-2">
                       <div className="p-1 bg-white dark:bg-neutral-800 rounded-full shadow-sm">
                            <Mail className="w-4 h-4 text-purple-700 dark:text-purple-400" />
                        </div>
                      <span className="font-medium">Email:</span>{" "}
                      <a
                        href="mailto:support@zapify.pro"
                        className="text-blue-600 dark:text-blue-400 underline"
                      >
                        support@zapify.pro
                      </a>
                    </li>
                    <li>
                      <span className="font-medium">Subject:</span> "Data
                      Deletion Request - Zapify"
                    </li>
                  </ul>
                  <div className="bg-white dark:bg-neutral-800/50 p-4 rounded-md border border-purple-100 dark:border-purple-800/30">
                      <p className="text-gray-700 dark:text-gray-300 text-base mb-2 font-medium">
                        In your email, please include:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 text-base">
                        <li>Your full name (as it appears on Instagram).</li>
                        <li>The URL of your Instagram profile.</li>
                        <li>Your registered email address.</li>
                      </ul>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 text-base mt-4 ml-1">
                    We will process your request within{" "}
                    <strong>7 business days</strong> and confirm once the
                    deletion is complete.
                  </p>
                </div>
            </section>

             <Separator className="bg-gray-100 dark:bg-neutral-800" />

            {/* Section 4 */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    4. Contact Information
                  </h2>
                </div>
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900 rounded-lg p-6 text-center">
                  <p className="text-gray-700 dark:text-gray-300 text-base mb-6">
                    If you have any further questions regarding data deletion,
                    feel free to reach out to us at{" "}
                    <a
                      href="mailto:support@zapify.pro"
                      className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      support@zapify.pro
                    </a>
                    .
                  </p>
                  
                  <Link to="/">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 dark:bg-blue-600 dark:hover:bg-blue-700">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {t.legal.returnToWebsite}
                    </Button>
                  </Link>
                </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
